import React from "react";
import { useHistory } from "react-router-dom";
import ProductType, {
    ClientProductTypes,
    ClientProductTypesList
} from "../../../../../common/interfaces/enums/ProductType";
import cx from "classnames";
import Checkbox from "../../../../../common/components/Forms/Checkbox/Checkbox";
import Icon from "../../../../../common/components/Icon/Icon";
import { IconSetIds } from "../../../../../common/components/Icon/IconMap";
import { getProductTypeAsString } from "../../../../../common/shared/enumHelpers";
import styles from "./QuoteSettingsEditor.module.scss";
import ToolTip from "../../../../../common/components/Tooltips/Tooltip";

interface IQuoteSettingsEditorProps {
    className?: string;
    isLoading?: boolean;
    hasSelectedProducts?: boolean;
    productState: ProductSelectionState;
    setProductState: (newState: ProductSelectionState) => void;
    title?: string;
    isFromDAP?: boolean;
    showWarning?: () => void;
    setWarningNextStepUrl?: (url: string) => void;
}
export type ProductSelectionState = {
    [key in ClientProductTypes]?: IProductEditorState;
};

export interface IProductEditorState {
    hasSettings?: boolean;
    isSelected: boolean;
    hasChanged: boolean;
}

const tooltipContent: { [key in ClientProductTypes] } = {
    [ProductType.Life]:
        "Have a lump sum paid out for your loved ones if the unexpected happens to you.",
    [ProductType.Trauma]:
        "Have a lump sum paid out for your loved ones if the unexpected happens to you.",
    [ProductType.Health]:
        "Get treatment faster. Skip the public waiting list and get back on your feet in no time.",
    [ProductType.Income]:
        "Protect your income if an unexpected illness stops you from being able to work.",
    [ProductType.Mortgage]:
        "Protect your mortgage payments in the event you're not able to work."
};

const alertErrorIcon: IconSetIds = "AlertError";

const QuoteSettingsEditor: React.FC<IQuoteSettingsEditorProps> = (props) => {
    const {
        productState,
        title,
        setProductState,
        isLoading,
        hasSelectedProducts,
        className
    } = props;

    const history = useHistory();

    return (
        <div
            className={cx("panel-card", "layout-product-list", "gap-m", {
                [className]: !!className,
                error: !hasSelectedProducts
            })}>
            {title && (
                <h2
                    className={cx("text-font-size-l", {
                        "text-error": !hasSelectedProducts
                    })}>
                    {title}
                </h2>
            )}
            {!hasSelectedProducts && (
                <div className={cx("layout-flex-row--centered", "gap-l")}>
                    <Icon id={alertErrorIcon} size="xl" className={cx("space-ml")} />
                    <p className={cx("text-error gap-none text-line-height-m")}>
                        Select one or more products to view quotes.
                    </p>
                </div>
            )}
            {ClientProductTypesList.map((product) => {
                const handleClick = (): void => {
                    const isQuoteWarningConfirm =
                        !!window.localStorage.getItem("quote-modified-warning") || false;
                    if (!isLoading && props.isFromDAP && !isQuoteWarningConfirm) {
                        props.setWarningNextStepUrl(`/${product}/settings`);
                        props.showWarning();
                        return;
                    }
                    if (!isLoading) history.push(`/${product}/settings`);
                };

                const handleChange = (): void => {
                    const isQuoteWarningConfirm =
                        !!window.localStorage.getItem("quote-modified-warning") || false;
                    if (!isLoading && props.isFromDAP && !isQuoteWarningConfirm) {
                        props.showWarning();
                        return;
                    }
                    setProductState({
                        ...productState,
                        [product]: {
                            ...productState[product],
                            isSelected: !productState[product]
                                ?.isSelected,
                            hasChanged: !productState[product]
                                ?.hasChanged
                        }
                    });
                };

                const isProductQuoted = productState[product]?.hasSettings;

                return (
                    <div
                        key={product}
                        className={cx(
                            "panel-product-selection",
                            "layout-flex-row--justify-spaced",
                            "layout-flex-row--centered",
                            styles["product-section"],
                            product,
                            {
                                ["cursor-pointer"]: !isProductQuoted && !isLoading,
                                ["fade-light"]: isLoading
                            }
                        )}>
                        <div
                            className={cx("layout-flex-row--centered")}
                            onClick={!isProductQuoted ? handleClick : undefined}>
                            {isProductQuoted ? (
                                <Checkbox
                                    className="space-l"
                                    checked={productState[product]?.isSelected}
                                    name={product}
                                    size="l"
                                    isDisabled={isLoading}
                                    onChange={handleChange}>
                                </Checkbox>
                            ) : (
                                <Icon id="AccordionPlus" size="xl" className="space-l" />
                            )}
                            <label
                                tabIndex={0}
                                className={cx("text-font-size-ml", "gap-vertical-none", {
                                    ["text-brand "]: !isProductQuoted
                                })}
                                htmlFor={product}>
                                {!isProductQuoted && "Add "}
                                {getProductTypeAsString(product, true)} cover
                            </label>
                        </div>
                        {isProductQuoted && (
                            <a
                                tabIndex={0}
                                className={cx({
                                    "cursor-default": isLoading,
                                    "disabled": !productState[product].isSelected
                                })}
                                onClick={
                                    productState[product]?.isSelected
                                        ? handleClick
                                        : undefined
                                }>
                                Edit
                            </a>
                        )}
                        {!isProductQuoted && (
                            <ToolTip tooltipContent={tooltipContent[product]} />
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default QuoteSettingsEditor;
